import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList, ViewChildren
} from "@angular/core";
import {DxNavigatorOptions} from "../../../domain/ui-options/dx-navigator/dx-navigator-options";
import {DxNavigationTreeNodeDataProvider} from "../../../domain/navigator/dx-navigation-tree-node-data-provider";
import {DxAppEvent} from "../../../../dx-core/domain/events/dx-app-event";
import {DxNavigationTreeNode} from "../../../domain/navigator/dx-navigation-tree-node";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {DxLogger} from "../../../../dx-core/services/dx-logger.service";
import {DxAppError} from "../../../../dx-core/domain/error/dx-app-error";
import {DxNavigatorModeType} from "../../../domain/navigator/dx-navigator-mode-type";
import {Subscription} from "rxjs";
import {Location} from "@angular/common";
import {DxNavigatorService} from "../../../services/dx-navigator.service";
import {DxHeadNavigationMenuComponent} from "./head-navigation-menu/dx-head-navigation-menu.component";
import {DxSideNavigationMenuComponent} from "./side-navigation-menu/dx-side-navigation-menu.component";


/**
 * Корневой комопнент навигатора.
 * @publicApi
 */
@Component({
  selector: 'dx-navigator',
  templateUrl: './dx-navigator.component.html',
  styleUrls: ['./dx-navigator.component.css'],
  providers: [DxNavigatorService]
})
export class DxNavigatorComponent implements OnInit, OnDestroy, AfterViewInit {
  /** Опции навигатора */
  @Input()
  public options: DxNavigatorOptions;

  /**
   * Элементы верхнего меню (если используется).
   * Этот массив должен быть ОБЯЗАТЕЛЬНО передан в случае, если навигатор работает в режиме:
   * "Шапка всему голова".
   */
  private _headMenuItems: DxNavigationTreeNodeDataProvider[];

  /**
   * Мапа, устанавливающшая соотвествие между элементом верхнего навигационного меню и его
   * "Деревом подразделов". Используется только в режиме работы "Шапка всему голова"
   */
  private _subMenusMap: Map<number, DxNavigationTreeNodeDataProvider[]>;

  /**
   * Элементы боковой навигационной панели.
   * Массив передается ТОЛЬКО если навигатор работает в режиме: "Боковая панель всему голова".
   * Например, шапка навигатора может быть скрыта (не используется), а боковая панель работает как
   * основной элемет навигации
   */
  private _sideMenuItems: DxNavigationTreeNodeDataProvider[];


  /** Флаг, регулирующий видимость боковой навигационной панели */
  private _isSideMenuShown: boolean = false;

  /** Флаг, регулирующий видимость верхней навигационной панели */
  private _isHeadMenuShown: boolean = false;

  /** Ссылка на нода-хозяина навигационной панели. */
  private _sideMenuOwner: DxNavigationTreeNode;

  /**
   * Флаг, сигнализирующий о необходимости восстановления выделения в
   * боковой навигационной панели после ее инициализации.
   */
  private _restoreSideMenuSelection: boolean = false;

  /** Сообщение о том что в конфиге роутера приложения не определен роут, который попытались открыть. */
  private _roueNotFoundMessage: string;

  /** Флаг для отслеживания того, что при очередном навигационном переходе был загружен лэйзи-модуль. */
  private _isLazyModuleLoaded: boolean = false;

  /** Хранилище подписок для корректной отписки на дестрое. */
  private _subscriptions: Subscription[];


  /**
   * Подавление восстановления состояния боковой панели при навигации по верхнему меню.
   * При этой навигации панель скрывается\показывается что приводит к отрабатываню ветки
   * ```
   * this.sideNavigationMenuQL.changes.subscribe(...)
   * ```
   * Так как при навигации по верхней панели восстанавливать состояние не нужно,
   * то нужно это подавить.
   */
  private _suppressStateRestore: boolean = false;

  /** Таймаут подавления осстановления состояния боковой панели при навигации по верхнему меню. */
  private _suppressStateRestoreTimeout: any;


  /** `QueryList` для получения инстанcа верхней навигационной панели. */
  @ViewChildren(DxHeadNavigationMenuComponent)
  public headNavigationMenuQL: QueryList<DxHeadNavigationMenuComponent>;
  /** Инстанc верхней навигационной панели. */
  public headNavigationMenu: DxHeadNavigationMenuComponent;

  /** `QueryList` для получения инстанcа верхней навигационной панели. */
  @ViewChildren(DxSideNavigationMenuComponent)
  public sideNavigationMenuQL: QueryList<DxSideNavigationMenuComponent>;
  /** Инстанc верхней навигационной панели. */
  public sideNavigationMenu: DxSideNavigationMenuComponent;


  // АХТУНГ!!!
  // Нюанс относительно активированного роута ActivatedRoute.
  // Он определяется отностительно метки <router-outlet></router-outlet> через которую был инжектирован
  // комопонент, в конструкторе которого инжектируется `private _route: ActivatedRoute`. То есть это не
  // динамически меняющийся параметр, а статичный! НО! Зато он содержит в себе всю иерархию всех роутов под собой,
  // включая то что получено из lazy-load модулей.
  constructor(private _router: Router,
              private _changeDetector: ChangeDetectorRef,
              private _location: Location,
              private _route: ActivatedRoute,
              private _logger: DxLogger,
              private _navigatorService: DxNavigatorService) {
    this._subscriptions = [];
  }


  ngOnInit(): void {
    this._init();
  }

  ngOnDestroy(): void {
    this._navigatorService.clearData();
    this._subscriptions.forEach(item => item.unsubscribe());
  }

  ngAfterViewInit(): void {
    const url = this._location.path(false);
    const segments: string[] = url.split("/");
    const lastSegment = segments[segments.length - 1];
    this.headNavigationMenuQL.changes.subscribe((menus: QueryList<DxHeadNavigationMenuComponent>)  => {
      if(!this.headNavigationMenu) {
        this.headNavigationMenu = menus.first;
        if(this._navigatorService.needRestoreState(segments)) {
          this._restoreState(segments);
          this._changeDetector.detectChanges();
        } else if(lastSegment === this.options.navigatorRoute) {
          this._goDefaultRoute();
        }
      }
    });

    this.sideNavigationMenuQL.changes.subscribe((menus: QueryList<DxSideNavigationMenuComponent>) => {
      if(!this.sideNavigationMenu && !this._suppressStateRestore && this._isSideMenuShown) {
        this.sideNavigationMenu = menus.first;
        if(this._restoreSideMenuSelection) {
          this.sideNavigationMenu.restoreSelection(this._navigatorService.segments);
          this._navigatorService.clearSegments();
          this._restoreSideMenuSelection = false;
        }
      }
    });

    if(lastSegment === this.options.navigatorRoute) {
      this._goDefaultRoute();
    }
  }

  @Input()
  set headMenuItems(value: DxNavigationTreeNodeDataProvider[]) {
    this._headMenuItems = value;
  }

  @Input()
  set sideMenuItems(value: DxNavigationTreeNodeDataProvider[]) {
    this._sideMenuItems = value;
  }

  @Input()
  set subMenusMap(value: Map<number, DxNavigationTreeNodeDataProvider[]>) {
    this._subMenusMap = value;
  }

  get roueNotFoundMessage(): string {
    return this._roueNotFoundMessage;
  }

  get isSideMenuShown(): boolean {
    return this._isSideMenuShown;
  }

  get isHeadMenuShown(): boolean {
    return this._isHeadMenuShown;
  }

  get headMenuItems(): DxNavigationTreeNodeDataProvider[] {
    return this._headMenuItems;
  }

  get sideMenuItems(): DxNavigationTreeNodeDataProvider[] {
    return this._sideMenuItems;
  }


  public onHomeClick(): void {
    if(this._isSideMenuShown) this.hideSideMenu();
    this._navigateByNode(this._navigatorService.homeNode, true);
  }


  public onOpenMenuItem(event: DxAppEvent): void {
    const isHeadNavigation: boolean = event.source === 'dx-head-navigation-menu';
    if(isHeadNavigation) this.hideSideMenu();
    const node = <DxNavigationTreeNode> event.data;
    this._navigateByNode(node, isHeadNavigation);
  }


  public onOpenSubMenu(event: DxAppEvent): void {
    this._suppressStateRestore = true;
    if(this._suppressStateRestoreTimeout) {
      clearTimeout(this._suppressStateRestoreTimeout);
    }
    this._suppressStateRestoreTimeout = setTimeout(() => {
      this._suppressStateRestore = false;
    }, 100);

    if(this._roueNotFoundMessage) this._roueNotFoundMessage = null;
    const node= <DxNavigationTreeNode> event.data;
    if(this._subMenusMap.has(node.id)) {
      this.showSideMenu(node);
      // По смыслу подменю осуществляет навигацию по подразделам верхнего пункта меню.
      // Соотвество пункты подменю должны быть дочерними роутами для роута верхнего пункта меню.
      // Поэтому, чтобы получить корректный путь при клике по элементу подменю, нужно сначала открыть
      // родительский роут. Лочно в общем.
      this._navigateByNode(node, true);
    } else {
      // Бам Бам ошибка
      this._isSideMenuShown = false;
    }
  }


  private _init(): void {
    this._navigatorService.options = this.options;
    this._navigatorService.eagerRouterConfig = this._router.config;
    this._navigatorService.rootActivatedRoute = this._route;
    this._navigatorService.subMenusMap = this._subMenusMap;
    this._navigatorService.init();

    // HOLY MAGIC OF setTimeout
    // Оборачивание в setTimeout сделано чтобы через использование асинхронной обертки
    // тригануть отложенное (относительно иницыализации основного компонета)
    // выволнение *ngIf показывающего шапку. Чтоб в свою очередь тригануть
    // ветку: this.headNavigationMenuQL.changes. Это позволяет иметь единый механизм обработки
    // для случаев когда меню в шапке есть всегда и когда оно показвается \ скрывается.
    // setTimeout(() => { this._isHeadMenuShown = true; }, 100);

    const navigatorRoute = this._navigatorService.navigatorRoute;
    if(navigatorRoute) {
      this._bind();
      this._navigatorService.collectRoutes(this._navigatorService.configuredRoutes, navigatorRoute);
    }
    // На самом деле такая ситуация возможна тогда, когда в конфиге роутера указали один роут
    // а в опциях навигатора другой роут. Это является критичной ошибкой работы системы.
    else {
      let msg: string = 'В конфигурации роутера приложения не найден корневой роут навигатора ';
      msg += '"' +  this.options.navigatorRoute + '" ';
      msg += 'Возможно название корневого роута навигатора не верно указано либо в опциях навигатора, ';
      msg += 'либо в конфигурации роутера.';
      throw new DxAppError(msg);
    }
  }


  /** Биндинг всяких слушателей. */
  private _bind(): void {
    let subscription = this._router.events.subscribe(event => {
      if(event instanceof NavigationEnd) this._onNavigationEnd();
    });
    this._subscriptions.push(subscription);

    subscription = <Subscription> this._location.subscribe(popState => {
      if(event.type === 'popstate') this._onPopState(popState);
    });
    this._subscriptions.push(subscription);
  }


  /** Обработчик завершения такта навигации. */
  private _onNavigationEnd(): void {
    if(this._isLazyModuleLoaded) {
      this._navigatorService.updateActivatedRoutes(this._router.routerState);
    }
  }


  /** Обработчик собития перехода по истории браузера. */
  private _onPopState(popSateEvent: any): void {
    const url: string = popSateEvent.url;
    const segments: string[] = url.split("/");
    const lastSegment = segments[segments.length - 1];
    if(this._navigatorService.needRestoreState(segments)) {
      this._restoreState(segments);
    } else if(lastSegment === this.options.navigatorRoute || lastSegment === this.options.homeRoute) {
      this.hideSideMenu();
      this._goDefaultRoute();
    }
  }


  /**
   * Восстанавливает состояние навигатора (отметки в верхем и боковом меню) на основании информации из url.
   * @param {string[]} segments - сегменты открываемой url.
   */
  private _restoreState(segments: string[]): void {
    switch (this.options.mode) {
      case DxNavigatorModeType.TOP: {
        this.headNavigationMenu.restoreSelection(segments);
        break;
      }
      case DxNavigatorModeType.SIDE: {
        this.sideNavigationMenu.restoreSelection(segments);
        break;
      }
      case DxNavigatorModeType.TOP_SIDE: {
        // Востановление:
        // 1. Сначала разберемся с верхней панелью и восстановим отметку в ней.
        const headMenuNode = this.headNavigationMenu.restoreSelection(segments);
        // 2. Далее на основе отметки в верхнем меню, решаем надо ли нет
        //    восстанавливать состояние боковой панели.
        if(this._navigatorService.needRestoreSideMenuState(headMenuNode, segments)) {
          if(this.sideNavigationMenu) {
            this.sideNavigationMenu.restoreSelection(segments);
          } else {
            this.showSideMenu(headMenuNode);
            this._navigatorService.segments = segments;
            this._restoreSideMenuSelection = true;
          }
        }
        // Если у роута на который мы переходим есть подменю, но роутов элементов подменю
        // нет в url, то нужно сбросить отметку в подменю на дефолт.
        // Пример: переход с /navigator/reporting/olap-report на /navigator/reporting
        else if (this._subMenusMap.has(headMenuNode.id)) {
          if(this.sideNavigationMenu) {
            this.sideNavigationMenu.restoreSelection();
          } else {
            this.showSideMenu(headMenuNode);
            this._navigatorService.segments = segments;
            this._restoreSideMenuSelection = true;
          }
        }
        // Иначе, скрываем боковое меню, так как у данного элемента верхнего меню его нет.
        else { this.hideSideMenu(); }
        break;
      }
      default:
        break;
    }
  }


  /** Открывает боковое навигационной меню. */
  public showSideMenu(owner: DxNavigationTreeNode): void {
    this._sideMenuOwner = owner;
    this._sideMenuItems = this._subMenusMap.get(owner.id);
    this._isSideMenuShown = true;
  }

  /** Скрывает боковую навигационную панель. */
  public hideSideMenu(): void {
    this._isSideMenuShown = false;
    this._sideMenuOwner = null;
    this.sideNavigationMenu = null;
  }


  /**
   * Осуществляет переход по роутам на основе ноды.
   */
  private _navigateByNode(node: DxNavigationTreeNode, isHeadNavigation: boolean): void {
    this._isLazyModuleLoaded = this._navigatorService.isLazyModuleLoaded(node.route);
    if(this._roueNotFoundMessage) this._roueNotFoundMessage = null;
    if(this._navigatorService.configuredRoutes.has(node.route)) {
      const relativeToRoute = this._navigatorService.findRelativeRoute(node, isHeadNavigation, this._sideMenuOwner);
      const url = this._navigatorService.buildNavigationUrl(node, isHeadNavigation, this._sideMenuOwner);
      this._router.navigateByUrl(url, {relativeTo: relativeToRoute});
    } else {
      this._roueNotFoundMessage = `Роут "${node.route}" не найден в конфигурации роутера приложения!`;
      this._navigateToRouteNotFoundState(node, isHeadNavigation);
    }
  }


  /**
   * Функция осуществляет нафигационный переход на родительский роут в случае если конфиг роута, на который
   * попытались перейти не найден. Вся соль тут в корректном определении родителя в зависимости от:
   * 1. Режима работы навигатора
   * 2. Собственно нода
   * 3. Источника события навигации: шапка \ боковая панель
   */
  private _navigateToRouteNotFoundState(node: DxNavigationTreeNode, isHeadNavigation: boolean): void {
    switch (this.options.mode) {
      case DxNavigatorModeType.TOP: {
        if(node.level === 1) {
          this._router.navigateByUrl('/' + this.options.navigatorRoute);
        } else if(node.level > 1) {
          this._goBackToParent(node.parent, isHeadNavigation);
        }
        break;
      }
      case DxNavigatorModeType.SIDE: {
        if(node.level === 1) {
          this._router.navigateByUrl('/' + this.options.navigatorRoute);
        } else if(node.level > 1) {
          this._goBackToParent(node.parent, isHeadNavigation);
        }
        break;
      }
      case DxNavigatorModeType.TOP_SIDE: {
        if(isHeadNavigation) {
          if(node.level === 1) {
            this._router.navigateByUrl('/' + this.options.navigatorRoute);
          } else if(node.level > 1) {
            this._goBackToParent(node.parent, isHeadNavigation);
          }
        } else {
          if(node.level === 1) {
            this._goBackToSidePanelOwner();
          } else if(node.level > 1) {
            if(node.level === 2 && !node.parent.involvedInRouting) {
              this._goBackToSidePanelOwner();
            }
            else {
              this._goBackToParent(node.parent, isHeadNavigation);
            }
          }
        }
        break;
      }
      default:
        this._router.navigateByUrl('/' + this.options.navigatorRoute);
    }
  }


  /**
   * Осуществляет переход на роут родителя. Вспомогательная функция. Сделана чтобы убрать дублирующий код.
   */
  private _goBackToParent(node: DxNavigationTreeNode, isHeadNavigation: boolean, relativeToRoot: boolean = false): void {
    const url = this._navigatorService.buildNavigationUrl(node.parent, isHeadNavigation, this._sideMenuOwner);
    let relativeToRoute = this._route;
    if(!relativeToRoot) {
      relativeToRoute = this._navigatorService.findRelativeRoute(node.parent, isHeadNavigation, this._sideMenuOwner);
    }
    this._router.navigateByUrl(url, {relativeTo: relativeToRoute});
  }

  /**
   * Осуществляет переход на роут элемента верхнего меню, для которого открыта навигационная панель.
   * Сделана чтобы убрать дублирующий код.
   */
  private _goBackToSidePanelOwner(): void {
    const url = '/' + this.options.navigatorRoute + '/' + this._sideMenuOwner.route;
    this._router.navigateByUrl(url, {relativeTo: this._route});
  }

  /** Осуществляет переход на дефолтный роут. */
  private _goDefaultRoute(): void {
    if(this.headNavigationMenu) this.headNavigationMenu.unselect();
    // Пока что пусть будет переход на хоум
    if(!this.options.defaultRoute && this.options.homeRoute) {
      this._navigateByNode(this._navigatorService.homeNode, true);
    }
  }
}
